// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/components/DeliveryWidget.tsx
// 🔧 PURPOSE: La consegna in corso, in cima a TUTTE le pagine: una RIGA che
//             occupa il suo spazio (il contenuto scorre sotto), non un popup.
//
//             Chi ha ordinato caffè & brioche o il vino vuole sapere
//             "quando arriva" senza aprire la chat. Quattro stati:
//             - piu' di un'ora prima: un chip sottile "🚚 Consegna oggi
//               alle 18:30", che non ruba spazio;
//             - nell'ultima ora: un banner con la barra che si riempie da
//               sola (0% un'ora prima, 95% all'orario fissato);
//             - passato l'orario, finche' lo staff non la segna: barra al
//               95% che pulsa, "Sta arrivando";
//             - consegnata: verde, 100%, per due ore (il server smette poi
//               di mandarla), poi sparisce.
//             Un tocco apre la chat, dove c'e' la card con tutti i dettagli.
// ============================================

import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { useGuestToken } from '../hooks/useGuestSession';
import useActiveDelivery from '../hooks/useActiveDelivery';
import { deliveryDayLabel, deliveryTimeLabel } from './DeliveryCard';
import type { ActiveDelivery } from '../lib/guestApi';
import { useStatusBarDeliveryBar, type StatusTint } from '../lib/statusBar';

const HOUR_MS = 60 * 60 * 1000;
/** La barra parte un'ora prima dell'orario fissato. */
const PROGRESS_WINDOW_MS = HOUR_MS;
/** A orario raggiunto la barra si ferma qui: al 100% ci va solo "consegnato". */
const PROGRESS_CAP = 95;
/** Il verde "consegnato" resta due ore (il server gia' filtra, qui per sicurezza). */
const DELIVERED_VISIBLE_MS = 2 * HOUR_MS;
/**
 * Se lo staff si dimentica di segnarla, "sta arrivando" non resta in cima
 * per sempre: tre ore dopo l'orario il widget si toglie (la card in chat
 * resta).
 */
const OVERDUE_HIDE_MS = 3 * HOUR_MS;
/** Ogni 30 secondi si ricalcola la barra (e il passaggio chip → banner). */
const TICK_MS = 30_000;

/** Pagine a tutto schermo (iframe, audio tour, accesso) dove sarebbe d'intralcio. */
const HIDDEN_PATH_PREFIXES = ['/taxi', '/audio-tour/', '/auto-login'];

/** Il fondo della riga (le stesse classi qui sotto): amber-50, bianco, emerald-50 e i loro scuri. */
function barTint(kind: WidgetState['kind']): StatusTint {
  if (kind === 'scheduled') return { light: '#fffbeb', dark: '#451a03' };
  if (kind === 'delivered') return { light: '#ecfdf5', dark: '#022c22' };
  return { light: '#ffffff', dark: '#111827' };
}

type WidgetState =
  | { kind: 'scheduled'; at: string }
  | { kind: 'inDelivery'; at: string; progress: number }
  | { kind: 'arriving'; at: string }
  | { kind: 'delivered' };

/** In quale dei quattro stati e' la consegna adesso (null = niente da mostrare). */
function widgetStateOf(d: ActiveDelivery, now: number): WidgetState | null {
  if (d.status === 'delivered') {
    const doneAt = Date.parse(d.deliveredAt || d.updatedAt);
    if (!isNaN(doneAt) && now - doneAt > DELIVERED_VISIBLE_MS) return null;
    return { kind: 'delivered' };
  }
  const atIso = d.at;
  if (!atIso) return null;
  const at = Date.parse(atIso);
  if (isNaN(at)) return null;
  const start = at - PROGRESS_WINDOW_MS;
  if (now < start) return { kind: 'scheduled', at: atIso };
  if (now < at) {
    const progress = ((now - start) / PROGRESS_WINDOW_MS) * PROGRESS_CAP;
    return { kind: 'inDelivery', at: atIso, progress: Math.max(0, Math.min(PROGRESS_CAP, progress)) };
  }
  if (now - at > OVERDUE_HIDE_MS) return null;
  return { kind: 'arriving', at: atIso };
}

const DeliveryWidget: React.FC = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const navigate = useNavigate();
  const location = useLocation();
  // Il Layout non si rimonta mai: il token arriva dallo store condiviso
  // della sessione, che avvisa a ogni accesso e uscita (anche da un'altra
  // scheda o per sessione scaduta). Prima lo si rileggeva a ogni cambio di
  // pagina e una volta al minuto, e la barra restava indietro fino a 60s.
  const token = useGuestToken();
  const delivery = useActiveDelivery(token);
  const [now, setNow] = useState(() => Date.now());

  // L'orologio del widget gira solo quando c'e' una consegna da mostrare.
  useEffect(() => {
    if (!delivery) return;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), TICK_MS);
    const onVisibility = () => { if (!document.hidden) setNow(Date.now()); };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [delivery]);

  // Visibilita' calcolata PRIMA dell'effetto qui sotto (gli hook devono
  // girare sempre nello stesso ordine).
  const path = location.pathname;
  const hiddenHere = HIDDEN_PATH_PREFIXES.some((p) => path.indexOf(p) === 0);
  const state = delivery && !hiddenHere ? widgetStateOf(delivery, now) : null;
  const visible = !!state;

  // La riga sta NEL FLUSSO della pagina (vedi Layout): occupa il suo spazio
  // sotto la barra di stato e spinge il contenuto piu' in basso, non gli
  // galleggia sopra. Le pagine pero' aggiungono da sole il margine per la
  // barra di stato (var(--safe-area-inset-top)): finche' la riga e' visibile
  // quel margine lo ha gia' pagato lei, quindi lo azzera per tutti, e
  // pubblica la propria altezza (--nva-bar-h) per l'intestazione fissa
  // della Home, che deve scendere di altrettanto.
  const barRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const root = document.documentElement;
    if (!visible) {
      root.style.removeProperty('--safe-area-inset-top');
      root.style.removeProperty('--nva-bar-h');
      return;
    }
    root.style.setProperty('--safe-area-inset-top', '0px');
    const publish = () => {
      const h = barRef.current ? Math.round(barRef.current.getBoundingClientRect().height) : 0;
      root.style.setProperty('--nva-bar-h', h + 'px');
    };
    publish();
    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined' && barRef.current) {
      ro = new ResizeObserver(publish);
      ro.observe(barRef.current);
    }
    return () => {
      if (ro) ro.disconnect();
      root.style.removeProperty('--safe-area-inset-top');
      root.style.removeProperty('--nva-bar-h');
    };
  }, [visible]);

  // La barra di stato prende il fondo della riga, finché è in cima.
  useStatusBarDeliveryBar(state ? barTint(state.kind) : null);

  if (!delivery || !state) return null;

  const openChat = () => navigate('/my-stay?open=chat');
  const openLabel = t('guestChat.delivery.widget.open');
  // Sotto la barra di stato dell'iPhone, a tutta larghezza, con lo sfondo
  // dell'app: e' una barra di stato, non un popup. Nell'app installata su
  // iPhone il suo contenuto scende anche dei 10px dello stile nativo
  // (--nva-top-edge, lib/statusBar), fuori dalla sfumatura di iOS: le pagine
  // sotto non li aggiungono (--safe-area-inset-top e' azzerato qui sopra).
  const rowStyle: React.CSSProperties = {
    paddingTop: 'calc(env(safe-area-inset-top, 0px) + var(--nva-top-edge, 0px))',
    paddingLeft: 'env(safe-area-inset-left)',
    paddingRight: 'env(safe-area-inset-right)',
  };

  // ---- Piu' di un'ora prima: una riga sottile ----
  if (state.kind === 'scheduled') {
    const rawDay = deliveryDayLabel(state.at, lang, t);
    // "Oggi"/"Domani" in mezzo alla frase vanno in minuscolo; i nomi dei
    // giorni restano come li scrive Intl (in tedesco la maiuscola e' giusta).
    const day = rawDay === t('guestChat.today') || rawDay === t('guestChat.delivery.tomorrow')
      ? rawDay.toLowerCase()
      : rawDay;
    const label = t('guestChat.delivery.widget.scheduled', { day, time: deliveryTimeLabel(state.at, lang) });
    return (
      <div ref={barRef} className="w-full flex-shrink-0 bg-amber-50 dark:bg-amber-950 border-b border-amber-200 dark:border-amber-800" style={rowStyle} aria-live="polite">
        <button
          type="button"
          onClick={openChat}
          aria-label={`${label}. ${openLabel}`}
          className="flex w-full items-center gap-2 px-4 py-2 text-left text-xs font-semibold text-amber-900 dark:text-amber-100 active:bg-amber-100 dark:active:bg-amber-900/60"
        >
          <span aria-hidden="true">🚚</span>
          <span className="min-w-0 flex-1 truncate">{label}</span>
          <ChevronRight className="h-3.5 w-3.5 flex-shrink-0 opacity-60" aria-hidden="true" />
        </button>
      </div>
    );
  }

  // ---- L'ultima ora, "sta arrivando" e "consegnato": la riga con la barra ----
  const delivered = state.kind === 'delivered';
  const progress = state.kind === 'inDelivery' ? state.progress : delivered ? 100 : PROGRESS_CAP;
  const label = state.kind === 'inDelivery'
    ? t('guestChat.delivery.widget.inDelivery', { time: deliveryTimeLabel(state.at, lang) })
    : state.kind === 'arriving'
      ? t('guestChat.delivery.widget.arriving')
      : t('guestChat.delivery.widget.delivered');
  const itemsText = delivery.items.join(', ');

  return (
    <div
      ref={barRef}
      className={`w-full flex-shrink-0 border-b ${
        delivered
          ? 'bg-emerald-50 border-emerald-200 dark:bg-emerald-950 dark:border-emerald-800'
          : 'bg-white border-amber-200 dark:bg-gray-900 dark:border-amber-800'
      }`}
      style={rowStyle}
      aria-live="polite"
    >
      <button
        type="button"
        onClick={openChat}
        aria-label={`${label}. ${openLabel}`}
        className="block w-full px-4 pt-2 pb-2.5 text-left active:bg-black/5 dark:active:bg-white/5"
      >
        <span className="flex items-center gap-2">
          <span aria-hidden="true" className="text-base leading-none">{delivered ? '✅' : '🚚'}</span>
          <span className={`min-w-0 flex-1 truncate text-sm font-bold ${
            delivered ? 'text-emerald-900 dark:text-emerald-100' : 'text-gray-900 dark:text-white'
          }`}>
            {label}
          </span>
          <ChevronRight className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" aria-hidden="true" />
        </span>
        {itemsText && (
          <span className="mt-0.5 block truncate text-[11px] leading-snug text-gray-600 dark:text-gray-300">
            {t('guestChat.delivery.widget.items', { items: itemsText })}
          </span>
        )}
        <span
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(progress)}
          className={`mt-2 block h-1.5 overflow-hidden rounded-full ${
            delivered ? 'bg-emerald-100 dark:bg-emerald-900/60' : 'bg-amber-100 dark:bg-amber-900/50'
          }`}
        >
          <span
            className={`block h-full rounded-full transition-[width] duration-1000 ease-linear ${
              delivered ? 'bg-emerald-500 dark:bg-emerald-400' : 'bg-amber-500 dark:bg-amber-400'
            } ${state.kind === 'arriving' ? 'animate-pulse' : ''}`}
            style={{ width: `${progress}%` }}
          />
        </span>
      </button>
    </div>
  );
};

export default DeliveryWidget;
